import React, { useState } from "react";
import PropTypes from "prop-types";
import { useTranslation } from "react-i18next";
import { Link } from "react-router-dom";
import "./customer-detail-modern.css";
import BackButton from "../Navigation/BackButton";
import Loader from "../Loader/Loader";
import NoDataConstruction from "../EmptyStates/NoDataConstruction";
import GoogleMapDark from "../Map/GoogleMapDark";
import ContactPersonManager from "./ContactPersonManager";
import { useContactPersons } from "../../hooks/useContactPersons";

function CustomerDetailView({
  customer,
  editableCustomer,
  setEditableCustomer,
  isEditing,
  setIsEditing,
  loading,
  error,
  onSaveClick,
  auth,
  projects = []
}) {
  const { t } = useTranslation();
  const {
    contactPersons,
    addContactPerson,
    updateContactPerson,
    deleteContactPerson
  } = useContactPersons(customer?.id);

  const [activeTab, setActiveTab] = useState('info');

  if (loading) return <Loader message={t('common.loading')} />;
  if (error) return <NoDataConstruction text={t('common.error')}/>;
  if (!customer) return <NoDataConstruction text={t('customers.noCustomerFound') || 'Klant niet gevonden!'}/>;

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setEditableCustomer((prev) => ({ ...prev, [name]: value }));
  };

  // Calculate active/archived projects using date-based logic (same as ProjectsContainer)
  const now = new Date();
  now.setHours(0, 0, 0, 0);

  const activeProjects = projects?.filter(p => {
    // If no end_date, project is active
    if (!p.end_date) return true;
    const endDate = new Date(p.end_date);
    endDate.setHours(0, 0, 0, 0);
    return endDate > now; // Active if end date is in the future
  }) || [];

  const archivedProjects = projects?.filter(p => {
    // If no end_date, project is not archived
    if (!p.end_date) return false;
    const endDate = new Date(p.end_date);
    endDate.setHours(0, 0, 0, 0);
    return endDate <= now; // Archived if end date is today or past
  }) || [];

  return (
    <div className="customer-detail-modern">
      <BackButton label={t('customers.title')} />

      {/* Modern Header with Customer Name and Actions */}
      <div className="customer-header">
        {isEditing && auth?.role === "eigenaar" ? (
          <div className="customer-name-edit">
            <input
              type="text"
              name="name"
              value={editableCustomer.name}
              onChange={handleInputChange}
              className="customer-name-input"
              placeholder={t('customers.customerName')}
            />
          </div>
        ) : (
          <h2>{customer.name}</h2>
        )}

        {auth?.role === "eigenaar" && (
          <div className="customer-header-actions">
            {!isEditing ? (
              <button
                type="button"
                className="btn btn-edit"
                onClick={() => setIsEditing(true)}
              >
                {t('customers.editCustomer')}
              </button>
            ) : (
              <>
                <button
                  type="button"
                  className="btn btn-save"
                  onClick={() => onSaveClick(editableCustomer)}
                >
                  {t('common.save')}
                </button>
                <button
                  type="button"
                  className="btn btn-cancel"
                  onClick={() => setIsEditing(false)}
                >
                  {t('common.cancel')}
                </button>
              </>
            )}
          </div>
        )}
      </div>

      {/* Desktop Tab Navigation */}
      <div className="tabs-container desktop-tabs">
        <button
          className={`tab ${activeTab === 'info' ? 'active' : ''}`}
          onClick={() => setActiveTab('info')}
        >
          {t('customers.customerDetails')}
        </button>
        <button
          className={`tab ${activeTab === 'contact' ? 'active' : ''}`}
          onClick={() => setActiveTab('contact')}
        >
          {t('customers.contactPersons')} <span className="tab-count">({contactPersons?.length || 0})</span>
        </button>
        <button
          className={`tab ${activeTab === 'projects' ? 'active' : ''}`}
          onClick={() => setActiveTab('projects')}
        >
          {t('common.projects')} <span className="tab-count">({projects?.length || 0})</span>
        </button>
        {customer.address && (
          <button
            className={`tab ${activeTab === 'location' ? 'active' : ''}`}
            onClick={() => setActiveTab('location')}
          >
            {t('customers.location')}
          </button>
        )}
      </div>

      {/* Mobile Tab Selector */}
      <select
        className="tab-selector mobile-tabs"
        value={activeTab}
        onChange={(e) => setActiveTab(e.target.value)}
        aria-label={t('common.selectTab')}
      >
        <option value="info">{t('customers.customerDetails')}</option>
        <option value="contact">{t('customers.contactPersons')} ({contactPersons?.length || 0})</option>
        <option value="projects">{t('common.projects')} ({projects?.length || 0})</option>
        {customer.address && (
          <option value="location">{t('customers.location')}</option>
        )}
      </select>

      {/* Tab Content */}
      <div className="tab-content">
        {/* Customer Info Tab */}
        {activeTab === 'info' && (
          <div className="info-grid">
            <div className="info-card">
              <h3>{t('customers.customerDetails')}</h3>
              <div className="info-rows">
                <div className="info-row">
                  <label>{t('common.address')}:</label>
                  {isEditing ? (
                    <input
                      name="address"
                      value={editableCustomer.address}
                      onChange={handleInputChange}
                      className="form-input"
                    />
                  ) : (
                    <span className="info-value">{customer.address || '-'}</span>
                  )}
                </div>

                <div className="info-row">
                  <label>{t('common.email')}:</label>
                  {isEditing ? (
                    <input
                      name="email"
                      type="email"
                      value={editableCustomer.email}
                      onChange={handleInputChange}
                      className="form-input"
                    />
                  ) : (
                    <span className="info-value">
                      {customer.email ? (
                        <a href={`mailto:${customer.email}`}>{customer.email}</a>
                      ) : '-'}
                    </span>
                  )}
                </div>

                <div className="info-row">
                  <label>{t('common.phone')}:</label>
                  {isEditing ? (
                    <input
                      name="phone"
                      type="tel"
                      value={editableCustomer.phone}
                      onChange={handleInputChange}
                      className="form-input"
                    />
                  ) : (
                    <span className="info-value">
                      {customer.phone ? (
                        <a href={`tel:${customer.phone}`}>{customer.phone}</a>
                      ) : '-'}
                    </span>
                  )}
                </div>

                <div className="info-row">
                  <label>{t('customers.kvkNumber')}:</label>
                  <span className="info-value">{customer.kvk_number || '-'}</span>
                </div>

                <div className="info-row">
                  <label>{t('customers.btwId')}:</label>
                  <span className="info-value">{customer.btw_id || '-'}</span>
                </div>

                {customer.contact_person && (
                  <div className="info-row">
                    <label>{t('customers.mainContactPersonLegacy')}:</label>
                    {isEditing ? (
                      <input
                        name="contact_person"
                        value={editableCustomer.contact_person || ''}
                        onChange={handleInputChange}
                        className="form-input"
                        placeholder={t('customers.mainContactPersonPlaceholder')}
                      />
                    ) : (
                      <span className="info-value">{customer.contact_person}</span>
                    )}
                  </div>
                )}
              </div>
            </div>
          </div>
        )}

        {/* Contact Persons Tab */}
        {activeTab === 'contact' && (
          <ContactPersonManager
            customerId={customer.id}
            contactPersons={contactPersons}
            onAdd={addContactPerson}
            onUpdate={updateContactPerson}
            onDelete={deleteContactPerson}
          />
        )}

        {/* Projects Tab */}
        {activeTab === 'projects' && (
          <div className="projects-section">
            {projects && projects.length > 0 ? (
              <>
                {activeProjects.length > 0 && (
                  <div className="projects-group">
                    <h3><span aria-hidden="true">✅</span> {t('projects.activeProjects')} ({activeProjects.length})</h3>
                    <div className="projects-grid">
                      {activeProjects.map(project => (
                        <Link
                          key={project.id}
                          to={`/projects/${project.id}`}
                          className="project-mini-card"
                        >
                          <div className="project-mini-header">
                            <h4>{project.name}</h4>
                            <span className="status-badge status-active">
                              {t('projects.active')}
                            </span>
                          </div>
                          {project.location && (
                            <p className="project-location">
                              <span aria-hidden="true">📍</span> {project.location}
                            </p>
                          )}
                          {project.start_date && project.end_date && (
                            <p className="project-dates">
                              <span aria-hidden="true">📅</span> {new Date(project.start_date).toLocaleDateString('nl-NL')} - {new Date(project.end_date).toLocaleDateString('nl-NL')}
                            </p>
                          )}
                        </Link>
                      ))}
                    </div>
                  </div>
                )}

                {archivedProjects.length > 0 && (
                  <div className="projects-group">
                    <h3><span aria-hidden="true">📦</span> {t('projects.archivedProjects')} ({archivedProjects.length})</h3>
                    <div className="projects-grid">
                      {archivedProjects.map(project => (
                        <Link
                          key={project.id}
                          to={`/projects/${project.id}`}
                          className="project-mini-card archived"
                        >
                          <div className="project-mini-header">
                            <h4>{project.name}</h4>
                            <span className="status-badge status-archived">
                              {t('projects.archived')}
                            </span>
                          </div>
                          {project.location && (
                            <p className="project-location">
                              <span aria-hidden="true">📍</span> {project.location}
                            </p>
                          )}
                        </Link>
                      ))}
                    </div>
                  </div>
                )}
              </>
            ) : (
              <NoDataConstruction text={t('projects.noProjectsFound')} />
            )}
          </div>
        )}

        {/* Location Tab */}
        {activeTab === 'location' && customer.address && (
          <div className="location-section">
            <div className="map-container">
              <GoogleMapDark
                address={customer.address}
                lat={customer.latitude}
                lng={customer.longitude}
              />
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

CustomerDetailView.propTypes = {
  customer: PropTypes.object,
  editableCustomer: PropTypes.object.isRequired,
  setEditableCustomer: PropTypes.func.isRequired,
  isEditing: PropTypes.bool.isRequired,
  setIsEditing: PropTypes.func.isRequired,
  loading: PropTypes.bool,
  error: PropTypes.string,
  onSaveClick: PropTypes.func.isRequired,
  auth: PropTypes.object,
  projects: PropTypes.array,
};

export default CustomerDetailView;
